import { Alert } from "antd";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { forwardRef, ReactNode, useCallback, useEffect, useState } from "react";
import "./Notification.css";

/**
 * Configuration for notification component animation timings
 * @constant
 */
const ANIMATION_DURATION = 500; // 0.5 seconds
const AUTO_CLOSE_DELAY = 5000; // 5 seconds

/**
 * Props for the Notification component
 * @interface NotificationProps
 * @property {ReactNode} message - The notification message content
 * @property {"info" | "success" | "warning" | "error"} [variant="info"] - Visual style variant
 * @property {"top" | "bottom"} [position="top"] - Position of the notification
 * @property {boolean} [autoClose=true] - Whether to automatically close the notification
 * @property {number} [autoCloseDelay=5000] - Delay in milliseconds before auto-closing
 * @property {boolean} [closable=true] - Whether the notification can be manually closed
 * @property {() => void} [onClose] - Callback fired when notification is closed
 * @property {ReactNode} [closeIcon] - Optional custom close icon
 * @property {string} [className] - Optional additional CSS class
 */
export interface NotificationProps
  extends VariantProps<typeof notificationVariants> {
  message: ReactNode;
  variant?: "info" | "success" | "warning" | "error";
  position?: "top" | "bottom";
  autoClose?: boolean;
  autoCloseDelay?: number;
  closable?: boolean;
  onClose?: () => void;
  closeIcon?: ReactNode;
  className?: string;
}

/**
 * notificationVariants - CVA configuration for Notification component variants and positions
 * Uses CSS custom properties that map to our design system tokens
 */
const notificationVariants = cva("indigo-notification-base", {
  variants: {
    variant: {
      info: "indigo-notification-info",
      success: "indigo-notification-success",
      warning: "indigo-notification-warning",
      error: "indigo-notification-error",
    },
    position: {
      top: "indigo-notification-top",
      bottom: "indigo-notification-bottom",
    },
  },
  defaultVariants: {
    variant: "info",
    position: "top",
  },
});

/**
 * Default close icon component
 * @returns {JSX.Element} Close icon SVG element
 */
const DefaultCloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="indigo-notification-close-icon"
  >
    <path
      d="M12 4L4 12M4 4L12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Notification component
 *
 * A notification component for displaying messages with different variants and animations
 * Built on top of Ant Design Alert component with design system styling
 * Supports auto-close functionality, custom close icons, and positioning
 * Available variants: info, success, warning, error
 * Available positions: top, bottom
 *
 * @example
 * ```tsx
 * <Notification
 *   message="Operation completed successfully"
 *   variant="success"
 *   position="top"
 *   autoClose={true}
 *   onClose={() => console.log('Notification closed')}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <Notification
 *   message={<span>Custom <strong>HTML</strong> content</span>}
 *   variant="warning"
 *   closable={false}
 *   autoClose={false}
 *   closeIcon={<CustomIcon />}
 * />
 * ```
 */
export const Notification = forwardRef<HTMLDivElement, NotificationProps>(
  (
    {
      message,
      variant = "info",
      position = "top",
      autoClose = true,
      autoCloseDelay = AUTO_CLOSE_DELAY,
      closable = true,
      onClose,
      closeIcon,
      className,
      ...props
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);

    /**
     * Handles notification close with animation
     * Triggers exit animation before removing from DOM
     */
    const handleClose = useCallback(() => {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, ANIMATION_DURATION);
    }, [onClose]);

    /**
     * Auto-close functionality
     * Sets up a timer to automatically close the notification after the specified delay
     */
    useEffect(() => {
      if (!autoClose) return;

      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }, [autoClose, autoCloseDelay, handleClose]);

    // Don't render if not visible
    if (!isVisible) return null;

    // Generate component className
    const notificationClass = notificationVariants({ variant, position });
    const animationClass = isAnimatingOut
      ? "indigo-notification-exit"
      : "indigo-notification-enter";
    const combinedClassName = clsx(
      notificationClass,
      animationClass,
      className,
    );

    // Map variant to Ant Design Alert type
    const alertType = variant === "error" ? "error" : variant;

    return (
      <div
        ref={ref}
        className={combinedClassName}
        role="alert"
        aria-live="polite"
        {...props}
      >
        <Alert
          message={message}
          type={alertType}
          closable={closable}
          closeIcon={closeIcon || <DefaultCloseIcon />}
          onClose={handleClose}
          showIcon={false}
          className="indigo-notification-alert"
        />
      </div>
    );
  },
);

Notification.displayName = "Notification";

export { notificationVariants };
